import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, Chip, Paper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  decodePaymentToken,
  PAYMENT_MODULE_CONFIG,
  PAYMENT_FAILURE_REASONS,
  DEFAULT_REDIRECT_PATH,
} from 'src/common/utils/paymentUtils';

export default function PaymentFailed() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token');
  const reason = searchParams.get('reason');
  const decoded = token ? decodePaymentToken(token) : null;
  const moduleConfig = decoded?.module ? PAYMENT_MODULE_CONFIG[decoded.module] : null;
  const redirectPath = moduleConfig?.redirectPath ?? DEFAULT_REDIRECT_PATH;
  const redirectLabel = moduleConfig?.redirectLabel ?? 'Go to Dashboard';
  const failureMessage =
    PAYMENT_FAILURE_REASONS[reason] ?? 'Something went wrong with your payment.';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
        px: 3,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 420,
          width: '100%',
          p: { xs: 4, sm: 5 },
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2.5,
        }}
      >
        <Box
          sx={{
            width: 96,
            height: 96,
            borderRadius: '50%',
            bgcolor: (theme) =>
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.error.main, 0.12)
                : alpha(theme.palette.error.main, 0.12),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: (theme) =>
              `0 0 0 12px ${alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.06 : 0.06)}`,
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 56, color: 'error.main' }} />
        </Box>

        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Payment Failed
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {failureMessage}
          </Typography>
        </Box>

        {decoded?.orderId && (
          <Chip
            label={`Order ID: ${decoded.orderId}`}
            size="small"
            variant="outlined"
            sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary', borderColor: 'divider' }}
          />
        )}

        <Button
          variant="contained"
          color="error"
          disableElevation
          fullWidth
          onClick={() => navigate(redirectPath)}
          sx={{ py: 1.25, borderRadius: 2, fontWeight: 600, fontSize: 15 }}
        >
          {redirectLabel}
        </Button>
      </Paper>
    </Box>
  );
}
